import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  RegistrationProvider,
  useRegistrationContext,
} from "../contexts/registrationContext";
import { strings } from "../assets/resources/translations";
import { uppercase } from "../services/utils/format";

import Header from "../components/Header";
import Basic from "../components/Basic";
import Customer from "../components/Customer";
import PriceSpecification from "../components/PriceSpecification";
import Extra from "../components/Extra";
import Button from "../elements/Button";
import Text from "../elements/Text";
import Alert from "../elements/Alert";

const RegistrationForm = ({ config, isPreviewForm }) => {
  const {
    wrapper: { inputs, removeInput, removeValidity },
    submit,
    isSubmitted,
    submitError,
    displayErrors,
  } = useRegistrationContext();

  const [hasParticipant, setHasParticipant] = useState(false);

  useEffect(() => {
    if (inputs.basic?.nrOfPax === "1") {
      if (hasParticipant) {
        removeInput("participant");
        removeValidity("participant");
        setHasParticipant(false);
      }
    } else if (inputs.basic?.nrOfPax === "2") {
      setHasParticipant(true);
    }
  }, [inputs, hasParticipant, removeInput, removeValidity]);

  return (
    <div className="booking-form">
      {!isSubmitted ? (
        <>
          <Header />
          <Basic name="basic" displayErrors={displayErrors} />
          <hr className="divider form-divider" />
          <Customer
            name="owner"
            type="lead"
            getContactInfo={true}
            displayErrors={displayErrors}
            staticEmail={isPreviewForm ? config?.userEmail : undefined} // Only prefill for preview
          />
          <hr className="divider form-divider" />
          {hasParticipant && (
            <>
              <Customer
                name="participant"
                type="participant"
                getContactInfo={false}
                displayErrors={displayErrors}
              />
              <hr className="divider form-divider" />
            </>
          )}
          <Extra
            name="extra"
            isDisabled={false}
            displayErrors={displayErrors}
          />
          <div className="form-footer">
            <PriceSpecification />
            <div className="booking-section">
              <p>{strings.recaptchaText}</p>
              <Button
                label={uppercase(strings.bookTrip)}
                onClick={() =>
                  submit(isPreviewForm ? config?.userEmail : undefined)
                }
              />
            </div>
            {submitError && <Alert message={submitError} type="error" />}
          </div>
        </>
      ) : (
        <div className="form-section">
          <Text
            type="lead"
            header={strings.successHeader}
            text={
              strings.successDescription +
              (config.event === "sport" ? strings.sportsSocial : "") + (config.event === "sport" ? strings.contactUsSports : strings.contactUsGroups)
            }
          />
        </div>
      )}
    </div>
  );
};

const Registration = ({ config = {}, isPreviewForm = false }) => {
  // Ensure config is always an object to avoid undefined errors
  const safeConfig = config || {};

  return (
    <RegistrationProvider init={safeConfig} isPreviewForm={isPreviewForm}>
      {!isPreviewForm ? (
        <Helmet>
          <title>{safeConfig.title}</title>
          <meta name="description" content={strings[safeConfig.description]} />
          <meta property="og:site_name" content={window.location.origin} />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={safeConfig.title} />
          <meta
            property="og:description"
            content={strings[safeConfig.description]}
          />
          <meta property="og:image" content={safeConfig.image} />
        </Helmet>
      ) : (
        <div>
          <Helmet>
            <title>
              Preview: {safeConfig.title || "Sports Trip Form"} - TUI
            </title>
          </Helmet>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "var(--color-functional-alert-800)",
                padding: "15px 20px",
                color: "white",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              PREVIEW
            </div>
          </div>
        </div>
      )}

      <RegistrationForm config={safeConfig} isPreviewForm={isPreviewForm} />
    </RegistrationProvider>
  );
};

export default Registration;
